import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import {
    dark_grey_blue,
    silver_two,
    faded_red,
    cool_grey,
    color_colors_ocean,
    color_variants_ocean_light_2,
    color_colors_decline,
    color_variants_red_2,
    color_shades_darkest,
    color_variants_ocean_light_1,
    color_variants_ocean_xlight,
    text_blue
} from '../../constants/colors';
import FieldError from './FieldError';
import Button from '../general/Button';
import IconTooltip from '../general/IconTooltip';
import TooltipWrapper from '../general/TooltipWrapper';
import ApplicationVariablesContainer from '../../hooks/ApplicationVariablesContainer';
import OrganizationVariablesContainer from '../../hooks/OrganizationVariablesContainer';
import ResponseVariableTestButton from '../general/ResponseVariableTestButton';
import ReferenceVariableEditorModal from './ReferenceVariableEditorModal';
import {
    ResolvedReferenceVariable,
    VariableReference,
    findVariableReferences,
    getResolvedReferenceVariablesByName,
    notifyReferenceVariableUpdated,
    resolveReferenceVariables,
    unwrapVariableReference,
    updateResolvedReferenceVariable
} from '../../utils/referenceVariables';

// Icons
const deleteIcon = require('../../content/images/trash.svg');
const editIcon = require('../../content/images/pencil.svg');

// Types and Interfaces
interface TextFieldProps {
    name?: string;
    type?: string;
    placeholder?: string;
    value?: string;
    disabled?: boolean;
    onChange?: (e: React.ChangeEvent<any>) => void;
    onBlur?: (e: any) => void;
    onFocus?: (e: any) => void;
    onSelectionChange?: (start: number, end: number) => void;
    onDelete?: () => void;
    label?: string;
    id?: string;
    canDelete?: boolean;
    required?: boolean;
    tooltip?: string;
    className?: string;
    labelClassName?: string;
    icon?: string;
    minRows?: number;
    maxRows?: number;
    useForm?: boolean;
    stopPropagation?: boolean;
    defaultValue?: string;
    onReset?: () => void;
    showResponseVariableTest?: boolean;
    embedded?: boolean;
}

interface TooltipState {
    x: number;
    y: number;
    visible: boolean;
    reference?: VariableReference;
}

const TextAreaField: React.FC<TextFieldProps> = (props) => {
    // Container hooks
    const appVariableContainer = ApplicationVariablesContainer.useContainer();
    const organizationVariableContainer = OrganizationVariablesContainer.useContainer();

    // State
    const [tooltip, setTooltip] = useState<TooltipState>({
        x: 0,
        y: 0,
        visible: false
    });
    const [editorReference, setEditorReference] = useState<VariableReference | null>(null);

    // Refs
    const contentEditableRef = useRef<HTMLDivElement>(null);
    const lastValueRef = useRef<string>('');
    const tooltipRef = useRef<HTMLDivElement>(null);

    const variablesByName = useMemo(
        () => getResolvedReferenceVariablesByName(
            appVariableContainer.applicationVariables || [],
            organizationVariableContainer.organizationVariables || []
        ),
        [
            appVariableContainer.applicationVariables,
            organizationVariableContainer.organizationVariables
        ]
    );

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                tooltip.visible
                && !tooltipRef.current?.contains(target)
                && !contentEditableRef.current?.contains(target)
            ) {
                setTooltip(current => ({ ...current, visible: false }));
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [tooltip.visible]);

    useEffect(() => {
        if (!tooltip.visible) {
            return;
        }

        const hidePopover = () => setTooltip(current => ({ ...current, visible: false }));
        window.addEventListener('resize', hidePopover);
        window.addEventListener('scroll', hidePopover, true);
        return () => {
            window.removeEventListener('resize', hidePopover);
            window.removeEventListener('scroll', hidePopover, true);
        };
    }, [tooltip.visible]);

    useEffect(() => {
        // Update content when the controlled value changes externally.
        if (contentEditableRef.current && props.value !== lastValueRef.current) {
            // Only update if the element doesn't have focus to avoid cursor jumping during typing
            if (document.activeElement !== contentEditableRef.current) {
                contentEditableRef.current.innerHTML = generateHighlightedHTML(props.value || '');
                lastValueRef.current = props.value || '';
            }
            // If element has focus, don't update anything - let handleContentChange manage it
        }
    }, [props.value]);

    useEffect(() => {
        if (!contentEditableRef.current) {
            return;
        }

        const hasFocus = document.activeElement === contentEditableRef.current;
        const currentCursorPos = hasFocus ? saveCursorPosition() : 0;
        const currentText = hasFocus
            ? htmlToText(contentEditableRef.current.innerHTML)
            : props.value || '';

        contentEditableRef.current.innerHTML = generateHighlightedHTML(currentText);
        lastValueRef.current = currentText;

        if (hasFocus) {
            setTimeout(() => {
                restoreCursorPosition(currentCursorPos);
            }, 0);
        }
    }, [variablesByName]);

    useEffect(() => {
        // Initialize content on mount
        if (contentEditableRef.current && props.value) {
            contentEditableRef.current.innerHTML = generateHighlightedHTML(props.value);
            lastValueRef.current = props.value;
        }

    }, []);

    // Computed values
    const showLabel = props.label !== undefined || props.canDelete || props.tooltip !== undefined || props.required;
    const showResetButton = props.defaultValue !== undefined && props.value !== props.defaultValue;

    // Event handlers
    const handleReset = () => {
        if (props.onReset) {
            props.onReset();
        } else if (props.onChange) {
            const syntheticEvent = {
                target: { value: props.defaultValue, name: props.name },
                currentTarget: { value: props.defaultValue, name: props.name }
            } as React.ChangeEvent<any>;
            props.onChange(syntheticEvent);
        }
    };

    // HTML/Text conversion utilities
    const generateHighlightedHTML = (text: string): string => {
        if (!text) return '';

        const references = findVariableReferences(text, variablesByName);

        // Helper function to escape HTML and preserve structure
        const escapeAndFormatText = (str: string): string => {
            return str.replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\n/g, '<br>');
        };

        if (references.length === 0) {
            // No variables, just escape HTML and convert newlines
            return escapeAndFormatText(text);
        }

        // Create a map of unique variable names to color indices
        const uniqueVarNames = new Set<string>();
        references.forEach(ref => {
            if (ref.isFound) {
                uniqueVarNames.add(ref.name);
            }
        });
        const varNameToColorIndex = new Map<string, number>();
        Array.from(uniqueVarNames).forEach((name, index) => {
            varNameToColorIndex.set(name, index);
        });

        let html = '';
        let lastIndex = 0;

        references.forEach((ref) => {
            // Add text before the variable
            if (ref.start > lastIndex) {
                const beforeText = text.substring(lastIndex, ref.start);
                html += escapeAndFormatText(beforeText);
            }

            // Get color index for this variable (alternate between 0 and 1)
            const colorIndex = ref.isFound ? varNameToColorIndex.get(ref.name) || 0 : 0;

            // Add the highlighted variable with color index (modulo 2 for alternating colors)
            const className = ref.isFound ? `highlighted-variable highlighted-variable-${colorIndex % 2}` : 'invalid-variable';

            // For display: resolve all nested variables to show the deepest value
            const displayText = ref.isFound
                ? resolveReferenceVariables(ref.value, variablesByName)
                : ref.text;

            const escapedDisplayText = displayText.replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\n/g, '<br>');

            // Store the original variable text for editing functionality
            html += `<span class="${className}" data-reference-start="${ref.start}" data-variable-text="${ref.text.replace(/"/g, '&quot;')}">${escapedDisplayText}</span>`;

            lastIndex = ref.end;
        });

        // Add remaining text
        if (lastIndex < text.length) {
            const remainingText = text.substring(lastIndex);
            html += escapeAndFormatText(remainingText);
        }

        return html;
    };

    const htmlToText = (html: string): string => {
        if (!html) return '';

        // Create a temporary div to parse HTML properly
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Walk through all nodes and extract text content properly
        const extractText = (node: Node, isFirstChild: boolean = false): string => {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.textContent || '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (element.tagName === 'BR') {
                    return '\n';
                } else if (element.tagName === 'DIV') {
                    // For DIV elements that contentEditable creates for new lines
                    // Check if the DIV is empty or contains only a BR
                    const hasOnlyBr = element.childNodes.length === 1 &&
                        element.firstChild?.nodeName === 'BR';

                    if (hasOnlyBr || element.childNodes.length === 0) {
                        // Empty DIV or DIV with just BR - represents a newline
                        return isFirstChild ? '' : '\n';
                    }

                    const content = Array.from(element.childNodes).map((child, idx) =>
                        extractText(child, idx === 0)
                    ).join('');

                    // Add newline before DIV content only if it's not the first element
                    return isFirstChild ? content : '\n' + content;
                } else if (element.tagName === 'P') {
                    // For P elements, similar logic
                    const content = Array.from(element.childNodes).map((child, idx) =>
                        extractText(child, idx === 0)
                    ).join('');
                    return isFirstChild ? content : '\n' + content;
                } else if (element.tagName === 'SPAN') {
                    // For SPAN elements, check if it's a highlighted variable
                    // If so, return the original variable text (e.g., {{variable.name}})
                    // instead of the displayed resolved value
                    const variableText = element.getAttribute('data-variable-text');
                    if (variableText && (element.classList.contains('highlighted-variable') || element.classList.contains('invalid-variable'))) {
                        return variableText;
                    }
                    // For other spans, just extract content normally
                    return Array.from(element.childNodes).map((child, idx) =>
                        extractText(child, idx === 0)
                    ).join('');
                } else {
                    // For other inline elements, just extract content
                    return Array.from(element.childNodes).map((child, idx) =>
                        extractText(child, idx === 0)
                    ).join('');
                }
            }
            return '';
        };

        const result = Array.from(tempDiv.childNodes).map((node, idx) =>
            extractText(node, idx === 0)
        ).join('');

        // A visually blank contentEditable div often leaves a trailing newline
        // (e.g. a lone <div><br></div>). Strip it so that an empty field returns
        // "" rather than "\n", keeping it consistent with how other field types
        // (e.g. native <textarea>) represent no user input.
        if (result.trim() === '') {
            return '';
        }

        return result;
    };

    // Content change handler - just update value
    const handleContentChange = (e: React.FormEvent<HTMLDivElement>) => {
        if (contentEditableRef.current && props.onChange) {
            const htmlContent = contentEditableRef.current.innerHTML;
            const textContent = htmlToText(htmlContent);

            // Update the lastValueRef immediately to prevent external updates
            lastValueRef.current = textContent;

            // Call onChange immediately for responsive UI
            const syntheticEvent = {
                target: { value: textContent, name: props.name },
                currentTarget: { value: textContent, name: props.name }
            } as React.ChangeEvent<any>;
            props.onChange(syntheticEvent);
        }
    };

    const getTextPosition = (node: Node, offset: number): number => {
        if (!contentEditableRef.current) return 0;

        const range = document.createRange();
        range.selectNodeContents(contentEditableRef.current);
        try {
            range.setEnd(node, offset);
        } catch (e) {
            return lastValueRef.current.length;
        }

        const tempDiv = document.createElement('div');
        tempDiv.appendChild(range.cloneContents());
        return htmlToText(tempDiv.innerHTML).length;
    };

    const emitSelectionChange = () => {
        if (!contentEditableRef.current || !props.onSelectionChange) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        if (!contentEditableRef.current.contains(range.commonAncestorContainer)) return;

        props.onSelectionChange(
            getTextPosition(range.startContainer, range.startOffset),
            getTextPosition(range.endContainer, range.endOffset)
        );
    };

    // Apply/update highlighting when field loses focus
    const handleBlur = (e: any) => {
        emitSelectionChange();

        if (contentEditableRef.current) {
            const currentHTML = contentEditableRef.current.innerHTML;
            const textContent = htmlToText(currentHTML);
            const newHTML = generateHighlightedHTML(textContent);

            // Verify that the round-trip conversion preserves the text exactly
            const verifyText = htmlToText(newHTML);

            // Only update if the text content is exactly the same (including newlines)
            if (verifyText === textContent &&
                verifyText.length === textContent.length &&
                (verifyText.match(/\n/g) || []).length === (textContent.match(/\n/g) || []).length) {

                // Update with highlighted content
                contentEditableRef.current.innerHTML = newHTML;
                lastValueRef.current = textContent;
            }
        }

        // Call the original onBlur if provided
        if (props.onBlur) {
            props.onBlur(e);
        }
    };

    // Keep highlighting when gaining focus, just save cursor position
    const handleFocus = (e: any) => {
        // Don't remove highlighting - just let the user type
        // The highlighting will be preserved until they blur

        // Call the original onFocus if provided
        if (props.onFocus) {
            props.onFocus(e);
        }
    };

    // Cursor position management utilities
    const saveCursorPosition = (): number => {
        if (!contentEditableRef.current) return 0;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return 0;

        const range = selection.getRangeAt(0);
        return getTextPosition(range.startContainer, range.startOffset);
    };

    const restoreCursorPosition = (position: number) => {
        if (!contentEditableRef.current) return;

        const walker = document.createTreeWalker(
            contentEditableRef.current,
            NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
            {
                acceptNode: (node) => {
                    if (node.nodeType === Node.TEXT_NODE) {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                    if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'BR') {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                    return NodeFilter.FILTER_SKIP;
                }
            }
        );

        let currentPosition = 0;
        let node;

        while (node = walker.nextNode()) {
            if (node.nodeType === Node.TEXT_NODE) {
                const textLength = node.textContent?.length || 0;
                if (currentPosition + textLength >= position) {
                    const range = document.createRange();
                    const selection = window.getSelection();

                    try {
                        const offset = Math.min(Math.max(0, position - currentPosition), textLength);
                        range.setStart(node, offset);
                        range.collapse(true);

                        selection?.removeAllRanges();
                        selection?.addRange(range);

                        // Ensure the contentEditable div has focus
                        contentEditableRef.current?.focus();
                    } catch (e) {
                        // If we can't set the exact position, set it at the end
                        try {
                            const range = document.createRange();
                            const selection = window.getSelection();
                            range.selectNodeContents(contentEditableRef.current);
                            range.collapse(false);
                            selection?.removeAllRanges();
                            selection?.addRange(range);
                        } catch (fallbackError) {
                            // Final fallback - just focus the element
                            contentEditableRef.current?.focus();
                        }
                    }
                    return;
                }
                currentPosition += textLength;
            } else if (node.nodeName === 'BR') {
                // <br> tags represent line breaks, count as 1 character
                if (currentPosition >= position) {
                    const range = document.createRange();
                    const selection = window.getSelection();

                    try {
                        range.setStartBefore(node);
                        range.collapse(true);

                        selection?.removeAllRanges();
                        selection?.addRange(range);

                        contentEditableRef.current?.focus();
                    } catch (e) {
                        // Could not restore cursor position at BR
                    }
                    return;
                }
                currentPosition += 1; // BR counts as one character (newline)
            }
        }

        // If position is beyond content, place cursor at end
        if (position >= currentPosition) {
            try {
                const range = document.createRange();
                const selection = window.getSelection();
                range.selectNodeContents(contentEditableRef.current);
                range.collapse(false);
                selection?.removeAllRanges();
                selection?.addRange(range);
            } catch (e) {
                contentEditableRef.current?.focus();
            }
        }
    };

    const replaceVariableWithValue = (reference: VariableReference) => {
        if (!contentEditableRef.current || !props.onChange) return;

        const currentText = props.value || '';
        const currentCursorPos = saveCursorPosition();
        const variableIndex = reference.start;
        if (currentText.substring(reference.start, reference.end) !== reference.text) return;

        const newText = unwrapVariableReference(currentText, reference);

        const syntheticEvent = {
            target: { value: newText, name: props.name },
            currentTarget: { value: newText, name: props.name }
        } as React.ChangeEvent<any>;

        props.onChange(syntheticEvent);

        setTimeout(() => {
            if (contentEditableRef.current) {
                contentEditableRef.current.innerHTML = generateHighlightedHTML(newText);
                lastValueRef.current = newText;

                const lengthDifference = reference.value.length - reference.text.length;
                let newCursorPos = currentCursorPos;
                if (currentCursorPos > variableIndex) {
                    if (currentCursorPos <= reference.end) {
                        newCursorPos = variableIndex + reference.value.length;
                    } else {
                        newCursorPos = currentCursorPos + lengthDifference;
                    }
                }

                restoreCursorPosition(newCursorPos);
            }
        }, 0);

        setTooltip(prev => ({ ...prev, visible: false }));
    };

    const handleEditorClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (props.disabled) {
            return;
        }

        const target = event.target as HTMLElement;
        const highlightedElement = target.closest('.highlighted-variable') as HTMLElement;
        if (!highlightedElement) {
            setTooltip(current => ({ ...current, visible: false }));
            return;
        }

        const start = Number(highlightedElement.getAttribute('data-reference-start'));
        const reference = findVariableReferences(props.value || '', variablesByName)
            .find(candidate => candidate.start === start);
        if (!reference?.isFound) {
            return;
        }

        setTooltip({
            x: event.clientX,
            y: event.clientY,
            visible: true,
            reference
        });
    };

    const handleSaveReference = async (
        reference: ResolvedReferenceVariable,
        value: string
    ) => {
        const result = await updateResolvedReferenceVariable(reference, value);
        if (result.resultType === 'Ok') {
            notifyReferenceVariableUpdated(result.data);
        }
        return result;
    };

    // Render component

    return (
        <OuterWrapper
            className={props.className}
            embedded={props.embedded}
            onClick={(e) => { props.stopPropagation ? e.stopPropagation() : null }}
        >
            {/* Label section */}
            {showLabel && (
                <LabelContainer>
                    <LabelSection>
                        {props.label !== undefined && (
                            <Label htmlFor={props.name} className={props.labelClassName}>
                                {props.label}
                            </Label>
                        )}
                        {props.canDelete && (
                            <Button
                                additionalClasses={deleteIconStyle}
                                type="button"
                                onClick={props.onDelete}
                                themes={["icon"]}
                                icon={deleteIcon}
                            />
                        )}
                        {props.tooltip !== undefined && (
                            <IconTooltip
                                text={props.tooltip}
                                className={tooltipStyle}
                                place="top"
                            />
                        )}
                        {props.required && (
                            <TooltipWrapper text="Required field">
                                <RequiredLabel>*</RequiredLabel>
                            </TooltipWrapper>
                        )}
                    </LabelSection>
                    {props.showResponseVariableTest && (
                        <ResponseVariableTestButton />
                    )}
                </LabelContainer>
            )}

            {/* Input field section */}
            <FieldContainer embedded={props.embedded}>
                {showResetButton && (
                    <ResetButton
                        type="button"
                        onClick={handleReset}
                    >
                        Reset to default
                    </ResetButton>
                )}
                {props.icon !== undefined && <img src={props.icon} />}
                <EditableTextField
                    ref={contentEditableRef}
                    contentEditable={!props.disabled}
                    hasIcon={props.icon != null}
                    embedded={props.embedded}
                    onInput={handleContentChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onClick={handleEditorClick}
                    onMouseUp={emitSelectionChange}
                    onKeyUp={emitSelectionChange}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.stopPropagation();
                        }
                    }}
                    suppressContentEditableWarning={true}
                    data-placeholder={props.placeholder}
                    style={{ minHeight: `${(props.minRows || 1) * 1.5}em` }}
                />
            </FieldContainer>

            {/* Tooltip popup */}
            {tooltip.visible && ReactDOM.createPortal((
                <TooltipPopup
                    ref={tooltipRef}
                    style={{
                        left: Math.max(130, Math.min(tooltip.x, window.innerWidth - 130)),
                        top: Math.max(72, tooltip.y - 8),
                        transform: 'translateX(-50%) translateY(-100%)',
                        position: 'fixed'
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                >
                    <TooltipContentWrapper>
                        <VariableName>{tooltip.reference?.name}</VariableName>
                        <TooltipActions>
                            <EditButton
                                type="button"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (tooltip.reference) {
                                        setEditorReference(tooltip.reference);
                                        setTooltip(current => ({ ...current, visible: false }));
                                    }
                                }}
                            >
                                <EditIcon src={editIcon} alt="" />
                                <EditButtonText>Edit</EditButtonText>
                            </EditButton>
                            <UnwrapButton
                                type="button"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (tooltip.reference) {
                                        replaceVariableWithValue(tooltip.reference);
                                    }
                                }}
                            >
                                Unwrap
                            </UnwrapButton>
                        </TooltipActions>
                    </TooltipContentWrapper>
                </TooltipPopup>
            ), document.body)}

            {/* Form error display */}
            {props.useForm && <FieldError name={props.name} />}

            {editorReference && (
                <ReferenceVariableEditorModal
                    reference={editorReference}
                    kind={editorReference.kind}
                    onClose={() => setEditorReference(null)}
                    onSave={handleSaveReference}
                />
            )}
        </OuterWrapper>
    );
};

const OuterWrapper = styled.div<{ className?: string, embedded?: boolean }>`
    margin-bottom: ${props => props.embedded ? '0' : '24px'};
    flex: ${props => props.embedded ? '1' : 'initial'};
    min-width: 0;

    &.tight {
        margin-bottom: 8px;
        label {
            margin: 8px 16px 0 16px;
        }
    }
`;

const LabelContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const LabelSection = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
`;

const Label = styled.label`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    text-align: left;
    margin: 0 16px;
`;

const RequiredLabel = styled.span`
    margin-right: 0;
    margin-left: auto;
    margin-top: 14px;
    margin-bottom: -20px;
    font-family: Muli;
    font-size: 24px;
    color: ${faded_red};
`;

const FieldContainer = styled.div<{ embedded?: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    border-radius: ${props => props.embedded ? '0' : '5px'};
    border: ${props => props.embedded ? 'none' : `solid 1px ${silver_two}`};
    background: white;
    margin-top: ${props => props.embedded ? '0' : '8px'};
    position: relative;
    
    img {
        margin: 16px;
    }
`;

const ResetButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    color: ${color_colors_ocean};
    font-family: Muli;
    font-size: 12px;
    cursor: pointer;
    z-index: 1;
    padding: 4px 8px;
    border-radius: 4px;
    text-decoration: none;
    
    &:hover {
        background-color: ${color_variants_ocean_light_1};
    }
`;

const EditableTextField = styled.div<{ hasIcon?: boolean, embedded?: boolean }>`
    color: ${dark_grey_blue};
    font-family: Muli;
    text-align: left;
    width: 100%;
    border: none; 
    padding: 8px 16px;
    font-size: 14px;
    margin: 12px 0;
    border-left: ${props => props.hasIcon ? `1px solid ${silver_two}` : 'none'};
    border-right: ${props => props.embedded ? `1px solid ${silver_two}` : 'none'};
    background: white;
    outline: none;
    min-height: 1.5em;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-y: auto;
    max-height: 300px;
    line-height: 1.4;
    
    &:empty::before {
        content: attr(data-placeholder);
        color: ${cool_grey};
        font-style: italic;
    }

    .highlighted-variable {
        border-radius: 3px;
        padding: 1px 2px;
        cursor: pointer;
        white-space: pre-wrap;
        word-wrap: break-word;
        color: ${text_blue};
    }

    /* Alternate between two ocean light colors for different variables */
    .highlighted-variable-0 {
        background-color: ${color_variants_ocean_light_2};
    }

    .highlighted-variable-1 {
        background-color: ${color_variants_ocean_xlight};
    }

    .invalid-variable {
        background-color: ${color_variants_red_2};
        color: ${color_colors_decline};
        border-radius: 3px;
        padding: 1px 2px;
        cursor: text;
        text-decoration: underline wavy ${color_colors_decline};
        white-space: pre-wrap;
        word-wrap: break-word;
    }

    br {
        display: block;
        margin: 0;
        line-height: 1.4;
    }
`;

const TooltipPopup = styled.div`
    position: fixed;
    background: ${color_shades_darkest};
    color: white;
    padding: 8px;
    border-radius: 6px;
    font-size: 16px;
    font-family: Muli;
    z-index: 1000;
    pointer-events: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: ${color_shades_darkest};
    }

    @media (max-width: 480px) {
        left: 12px !important;
        right: 12px;
        top: auto !important;
        bottom: max(12px, env(safe-area-inset-bottom));
        transform: none !important;

        &::after {
            display: none;
        }
    }
`;

const TooltipContentWrapper = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
`;

const TooltipActions = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
`;

const VariableName = styled.span`
    font-family: 'Courier New', monospace;
    font-size: 13px;
    font-weight: 600;
    padding: 2px 6px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
`;

const EditButton = styled.button`
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: auto;
    width: auto;
    height: 32px;
    flex-shrink: 0;
    transition: background-color 0.2s;
    white-space: nowrap;

    &:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    &:active {
        background-color: rgba(255, 255, 255, 0.3);
    }
`;

const UnwrapButton = styled(EditButton)`
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
`;

const EditIcon = styled.img`
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
`;

const EditButtonText = styled.span`
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
`;

const deleteIconStyle = css`
    height: 14px;
    margin: 0;
    margin-top: 24px;
    margin-bottom: 0;
    
    img {
        height: 14px;
    }
`;

const tooltipStyle = css`
    margin-left: 8px;
`;

export default TextAreaField;
